import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { VehicleSocket } from "../../../../../store/admin/vehiclesSocketStore";
import { useTranslation } from "react-i18next";
import { useTrackingLinkModalStore } from "../../../../../store/admin/trackingLinkModalStore";
import { useTrackingLinksStore, type TrackingLink } from "../../../../../store/admin/trackingLinksStore";

interface ShareContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

export default function ShareContent({
  vehicle,
  isDarkMode,
}: ShareContentProps) {
  const { t } = useTranslation("lite");
  const labelColor = isDarkMode ? "text-gray-500" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const buttonBg = "bg-primary-500 hover:bg-[#5fd43a] text-black";
  const cardBg = isDarkMode ? "bg-gray-800" : "bg-gray-100";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";

  const navigate = useNavigate();
  const openModal = useTrackingLinkModalStore((state) => state.openModal);
  const openDetailsModal = useTrackingLinkModalStore((state) => state.openDetailsModal);
  const trackingLinks = useTrackingLinksStore((state) => state.trackingLinks);

  // Filter tracking links that include this vehicle
  const vehicleTrackingLinks = useMemo(() => {
    return trackingLinks.filter((link: TrackingLink) => 
      link.units && link.units.includes(vehicle.id)
    );
  }, [trackingLinks, vehicle.id]);

  // Show only the last 2 tracking links
  const displayedLinks = useMemo(() => {
    return vehicleTrackingLinks.slice(0, 2);
  }, [vehicleTrackingLinks]);

  const handleGenerateLink = () => {
    openModal(vehicle);
  };

  const handleCopyLink = (publicUrl: string) => {
    // Ensure full URL is copied (publicUrl may be relative like "/trackingLinks/public?hash=...")
    const fullUrl = publicUrl.startsWith("http") ? publicUrl : `${window.location.origin}${publicUrl}`;
    navigator.clipboard.writeText(fullUrl);
  };

  const getStatusColor = (status: TrackingLink["status"]) => {
    if (!status) return "bg-gray-500";
    const statusName = status.name?.toLowerCase();
    if (statusName === "active" || statusName === "activo") return "bg-green-500";
    if (statusName === "expired" || statusName === "expirado") return "bg-red-500";
    if (statusName === "pending" || statusName === "pendiente") return "bg-yellow-500";
    return "bg-gray-500";
  };

  return (
    <div className={`p-3 border-t ${borderColor}`}>
      <h4 className="text-primary-500 font-semibold text-xs mb-2">
        {t("shareContent.title")}
      </h4>
      <div className="space-y-2 text-[10px]">
        <button 
          className={`w-full px-3 py-2 rounded text-[10px] font-medium ${buttonBg} flex items-center justify-center gap-2`}
          onClick={handleGenerateLink}
        >
          <i className="ri-link text-xs"></i>
          {t("shareContent.generateLink")}
        </button>
        
        {/* List of existing tracking links for this vehicle (last 2) */}
        {displayedLinks.length > 0 && (
          <div className="mt-3">
            <p className={`${labelColor} text-[9px] mb-2 font-medium`}>
              Links existentes ({vehicleTrackingLinks.length})
            </p>
            <div className="space-y-1.5">
              {displayedLinks.map((link) => (
                <div 
                  key={link.id} 
                  className={`${cardBg} rounded p-2 flex items-center justify-between gap-2 cursor-pointer hover:opacity-80 transition-opacity`}
                  onClick={() => openDetailsModal(link)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(link.status)}`}></span>
                      <span className={`${textColor} text-[9px] font-medium truncate`}>
                        {link.name}
                      </span>
                    </div>
                    {link.expirationDate && (
                      <p className={`${labelColor} text-[8px] mt-0.5 truncate`}>
                        Expira: {new Date(link.expirationDate._seconds * 1000).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCopyLink(link.publicUrl); }}
                      className={`p-1 rounded ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"} transition-colors`}
                      title="Copiar enlace"
                    >
                      <i className={`ri-file-copy-line text-[10px] ${textColor}`}></i>
                    </button>
                    <a
                      href={link.publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className={`p-1 rounded ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"} transition-colors`}
                      title="Abrir enlace"
                    >
                      <i className={`ri-external-link-line text-[10px] ${textColor}`}></i>
                    </a>
                  </div>
                </div>
              ))}
            </div>
            {vehicleTrackingLinks.length > 2 && (
              <button
                onClick={() => navigate("/admin/tracking-links")}
                className={`w-full mt-2 px-3 py-1.5 rounded text-[9px] font-medium ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-300" : "bg-gray-200 hover:bg-gray-300 text-gray-700"} transition-colors flex items-center justify-center gap-1`}
              >
                Ver más
                <i className="ri-arrow-right-s-line text-[10px]"></i>
              </button>
            )}
          </div>
        )}

        {vehicleTrackingLinks.length === 0 && (
          <p className={`${labelColor} text-[9px] mt-1`}>
            {t("shareContent.linkDescription")}
          </p>
        )}
      </div>
    </div>
  );
}